import { createContext, useContext } from 'react'

// Types
import { ICompany } from '../common/models/Company'
import { ICompanyMetadataPure } from '../common/models/CompanyMetadata'
import { ICompanyFeedbackAggregation } from '../common/models/Feedback'
import { IAlertPopup, INotification } from '../common/models/IAlertPopup'
import { INotificationPure } from '../common/models/Notification'
import { IGoal, IPdp } from '../common/models/Pdp'
import { IRequest } from '../common/models/Request'
import { IUser } from '../common/models/User'
import { AnyColor, ThemeColor } from '../common/models/colors'
import { IOnboardingInstancePopulated } from '../common/models/OnboardingInstance'
import { IMemberReleaseNotesState } from '../common/models/ReleaseNote'

// Styles
import { baseColors, createColors } from '../styles/colors'

export type UserAuthToken = string | undefined

export interface ITheme {
  id: number
  baseColors: {
    [key in ThemeColor]: number[]
  }
  colors: {
    [key in AnyColor]: string
  }
}

export interface ILoadingState {
  shouldLoadUser: boolean
  shouldLoadCompany: boolean
  shouldLoadRequests: boolean
  shouldLoadNotifications: boolean
  // Bumped by every request to reload the member's own PDP (see AsyncInitializer)
  personalPdpRequests: number
  shouldLoadNewToken: boolean
  shouldLoadFeedbackStatistics: boolean
  shouldLoadFeedbackAggregations: boolean
  shouldLoadOnboardingInstance: string | undefined
}

// Types
export type State = {
  loadingState: ILoadingState
  userAuthToken?: UserAuthToken
  user?: IUser
  theme: ITheme
  company?: ICompany
  newCompanySlug?: string
  userPdp?: IPdp
  alertPopup?: IAlertPopup
  notification?: INotification
  requests: IRequest[]
  // The request whose modal is open, wherever it was clicked (see RequestModals)
  requestModal?: IRequest
  notifications: INotificationPure[]
  feedbackAggregations?: ICompanyFeedbackAggregation
  companyMetaData?: ICompanyMetadataPure
  onboardingInstance?: IOnboardingInstancePopulated
  releaseNotes?: IReleaseNotesAppState
}

// Release-notes state synced for one company; autoOpened guards one popup per session
export interface IReleaseNotesAppState {
  companyId: string
  state: IMemberReleaseNotesState | null
  autoOpened: boolean
}

export const DEFAULT_APP_STATE: State = {
  loadingState: {
    shouldLoadUser: true,
    shouldLoadCompany: false,
    shouldLoadRequests: true,
    shouldLoadNotifications: true,
    personalPdpRequests: 1,
    shouldLoadNewToken: false,
    shouldLoadFeedbackStatistics: true,
    shouldLoadFeedbackAggregations: false,
    shouldLoadOnboardingInstance: undefined,
  },
  theme: {
    id: 0,
    baseColors,
    colors: createColors(baseColors),
  },
  requests: [],
  notifications: [],
  userAuthToken: undefined,
}

// Actions
export type updateStateAction = {
  type: 'updateState'
  payload: State
}
export type updateUserAction = {
  type: 'updateUser'
  payload: IUser | undefined
}

export type updateThemeAction = {
  type: 'updateTheme'
  payload: ITheme
}

export type updateCompanyAction = {
  type: 'updateCompany'
  payload?: ICompany
}

export type updateNewCompanySlugAction = {
  type: 'updateNewCompanySlug'
  payload: string | undefined
}

export type updateUserTokenAction = {
  type: 'updateUserToken'
  payload: UserAuthToken
}

export type updatePdpAction = {
  type: 'updatePdp'
  payload: IPdp
}

export type updateShouldLoadCompanyAction = {
  type: 'updateShouldLoadCompany'
  payload: boolean
}

export type updateShouldLoadUserAction = {
  type: 'updateShouldLoadUser'
  payload: boolean
}

export type updateShouldLoadction = {
  type: 'updateShouldLoad'
  payload: ILoadingState
}

export type updateShouldLoadPersonalPdpAction = {
  type: 'updateShouldLoadPersonalPdp'
  payload: true
}

export type updateShouldLoadNewTokenAction = {
  type: 'updateShouldLoadNewToken'
  payload: boolean
}

export type updateShouldLoadRequestAction = {
  type: 'updateShouldLoadRequests'
  payload: boolean
}

export type updateShouldLoadNotificationsAction = {
  type: 'updateShouldLoadNotifications'
  payload: boolean
}

export type updateShouldLoadFeedbackStatisticsAction = {
  type: 'updateShouldLoadFeedbackStatistics'
  payload: boolean
}

export type updateShouldLoadFeedbackAggregations = {
  type: 'updateShouldLoadFeedbackAggregations'
  payload: boolean
}

export type updateShouldLoadOnboardingInstance = {
  type: 'updateShouldLoadOnboardingInstance'
  payload: string | undefined
}

export type updatePersonalGoalsAction = {
  type: 'updatePersonalGoals'
  payload: IGoal[]
}

export type updateAlertPopupAction = {
  type: 'updateAlertPopup'
  payload?: IAlertPopup
}

export type updateNotificationAction = {
  type: 'updateNotification'
  payload?: INotification
}

export type updateUserPdpAction = {
  type: 'updateUserPdp'
  payload?: IPdp
}

export type updateUserRequestsAction = {
  type: 'updateUserRequests'
  payload: IRequest[]
}

export type updateRequestModalAction = {
  type: 'updateRequestModal'
  payload?: IRequest
}

export type updateNotificationsAction = {
  type: 'updateNotifications'
  payload: INotificationPure[]
}

export type updateFeedbackAggregationsAction = {
  type: 'updateFeedbackAggregations'
  payload: ICompanyFeedbackAggregation
}

export type updateCompanyMetaDataAction = {
  type: 'updateCompanyMetaData'
  payload: ICompanyMetadataPure
}

export type updateOnboardingInstanceAction = {
  type: 'updateOnboardingInstance'
  payload: IOnboardingInstancePopulated
}

export type updateReleaseNotesAction = {
  type: 'updateReleaseNotes'
  payload: IReleaseNotesAppState
}

export type Action =
  | updateStateAction
  | updateUserAction
  | updateThemeAction
  | updateCompanyAction
  | updateNewCompanySlugAction
  | updateUserTokenAction
  | updateUserPdpAction
  | updateShouldLoadRequestAction
  | updateShouldLoadNotificationsAction
  | updateShouldLoadCompanyAction
  | updateShouldLoadUserAction
  | updateShouldLoadction
  | updateShouldLoadPersonalPdpAction
  | updateShouldLoadNewTokenAction
  | updateShouldLoadFeedbackStatisticsAction
  | updateShouldLoadFeedbackAggregations
  | updatePersonalGoalsAction
  | updateAlertPopupAction
  | updateNotificationAction
  | updateUserRequestsAction
  | updateRequestModalAction
  | updateNotificationsAction
  | updateFeedbackAggregationsAction
  | updateCompanyMetaDataAction
  | updateOnboardingInstanceAction
  | updateShouldLoadOnboardingInstance
  | updateReleaseNotesAction

export type Dispatch = (action: Action) => void

// Contexts
export const AppStateCtx = createContext<State | undefined>(undefined)

export const AppDispatchCtx = createContext<Dispatch | undefined>(undefined)

// Reducer
export const appReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'updateState': {
      return {
        ...action.payload,
      }
    }
    case 'updateUser': {
      return {
        ...state,
        user: action.payload,
      }
    }
    case 'updateTheme': {
      return {
        ...state,
        theme: action.payload,
      }
    }
    case 'updateCompany': {
      return {
        ...state,
        company: action.payload,
      }
    }
    case 'updateNewCompanySlug': {
      return {
        ...state,
        newCompanySlug: action.payload,
      }
    }
    case 'updateUserToken': {
      return {
        ...state,
        userAuthToken: action.payload,
      }
    }
    case 'updateShouldLoadRequests': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadRequests: action.payload,
        },
      }
    }
    case 'updateShouldLoadNotifications': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadNotifications: action.payload,
        },
      }
    }
    case 'updateShouldLoadCompany': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadCompany: action.payload,
        },
      }
    }
    case 'updateShouldLoadUser': {
      return {
        ...state,
        loadingState: { ...state.loadingState, shouldLoadUser: action.payload },
      }
    }
    case 'updateShouldLoadPersonalPdp': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          personalPdpRequests: state.loadingState.personalPdpRequests + 1,
        },
      }
    }
    case 'updateShouldLoadNewToken': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadNewToken: action.payload,
        },
      }
    }
    case 'updateShouldLoadFeedbackStatistics': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadFeedbackStatistics: action.payload,
        },
      }
    }
    case 'updateShouldLoadFeedbackAggregations': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadFeedbackAggregations: action.payload,
        },
      }
    }
    case 'updateShouldLoadOnboardingInstance': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          shouldLoadOnboardingInstance: action.payload,
        },
      }
    }
    case 'updateShouldLoad': {
      return {
        ...state,
        loadingState: { ...state.loadingState, ...action.payload },
      }
    }
    case 'updatePersonalGoals': {
      return {
        ...state,
        personalGoals: action.payload,
      }
    }
    case 'updateAlertPopup': {
      return {
        ...state,
        alertPopup: action.payload,
      }
    }
    case 'updateNotification': {
      return {
        ...state,
        notification: action.payload,
      }
    }
    case 'updateUserPdp': {
      return {
        ...state,
        userPdp: action.payload,
      }
    }
    case 'updateUserRequests': {
      return {
        ...state,
        requests: action.payload,
      }
    }
    case 'updateRequestModal': {
      return {
        ...state,
        requestModal: action.payload,
      }
    }
    case 'updateNotifications': {
      return {
        ...state,
        notifications: action.payload,
      }
    }
    case 'updateFeedbackAggregations': {
      return {
        ...state,
        feedbackAggregations: action.payload,
      }
    }
    case 'updateCompanyMetaData': {
      return {
        ...state,
        companyMetaData: action.payload,
      }
    }
    case 'updateOnboardingInstance': {
      return {
        ...state,
        onboardingInstance: action.payload,
      }
    }
    case 'updateReleaseNotes': {
      return {
        ...state,
        releaseNotes: action.payload,
      }
    }
    default: {
      throw new Error(`Unhandled action type`)
    }
  }
}

// Hooks
export const useAppState = () => {
  const context = useContext(AppStateCtx)

  if (!context) {
    throw new Error('useAppState must be used within a AppProvider')
  }

  return context
}

export const useAppDispatch = () => {
  const context = useContext(AppDispatchCtx)

  if (!context) {
    throw new Error('useAppDispatch must be used within a AppProvider')
  }

  return context
}
